import React from 'react';
import { Button, Icon, TextInput } from '../../../components/ui';
import UserEditor from '../editors/UserEditor';
import { roleLabel } from '../../../roleLabels';

/*
 * UsersTab — the "Users" tab body for a Customer (or Subscriber) node in the unified Setup
 * pane. This IS the old "Users & Guards" section, scoped to the selected place: every person
 * for that customer — admins AND guards AND viewers — in one filterable list, with "+ Add user"
 * (creates ANY role here). Selecting a person opens the shared UserEditor inline (with a
 * back-to-list link); "+ Add user" opens it in create mode pre-scoped to this place.
 *
 * Props: scope {customerId? | subscriberId? | platform? (users with no subscriber — Super only)}, all users/lists + UserEditor deps, onChanged()
 * (bubbles a save/delete up so the container refreshes the tree badges), onDirtyChange(bool).
 */
export default class UsersTab extends React.Component {
  state = { selected: null, search: '', roleFilter: '' }; // selected: userId | 'new' | null

  componentDidMount() { this.maybeOpenPending(); }

  componentDidUpdate(prevProps) {
    // if the place we're scoped to changes, drop any open editor / filters
    if (prevProps.scope !== this.props.scope &&
        (prevProps.scope.customerId !== this.props.scope.customerId || prevProps.scope.subscriberId !== this.props.scope.subscriberId
          || prevProps.scope.platform !== this.props.scope.platform)) {
      this.setState({ selected: null, search: '', roleFilter: '' });
      if (this.props.onDirtyChange) this.props.onDirtyChange(false);
    }
    // a person picked from the org search asked to open here — open their editor once they're in scope
    if (this.props.openUserId != null) this.maybeOpenPending();
  }

  // open the search-selected person's editor (once the user is in this tab's scope), then tell the
  // container to clear the pending flag so it doesn't re-open on later renders.
  maybeOpenPending = () => {
    const uid = this.props.openUserId;
    if (uid == null || this.state.selected === uid) return;
    if (this.scopedUsers().some((u) => u.id === uid)) {
      this.setState({ selected: uid });
      if (this.props.onOpenedUser) this.props.onOpenedUser();
    }
  };

  scopedUsers = () => {
    const { scope } = this.props;
    return (this.props.users || []).filter((u) => {
      if (u.deletedAt) return false;
      if (scope.platform) return !u.GateSubscriber;   // [86bcfugmv] Dividia · Platform: users with no subscriber
      if (scope.customerId) return u.GateCustomer && String(u.GateCustomer.id) === String(scope.customerId);
      if (scope.subscriberId) return u.GateSubscriber && String(u.GateSubscriber.id) === String(scope.subscriberId);
      return false;
    });
  };

  open = (userId) => { if (this.props.guardNav) this.props.guardNav(() => this.setState({ selected: userId })); else this.setState({ selected: userId }); };
  // "Back to users" link — guarded (navigation away from unsaved edits should warn)
  back = () => { if (this.props.guardNav) this.props.guardNav(() => this.setState({ selected: null })); else this.setState({ selected: null }); };
  // the editor's Cancel button IS the discard, so it closes WITHOUT a redundant discard confirm.
  cancel = () => { this.setState({ selected: null }); if (this.props.onDirtyChange) this.props.onDirtyChange(false); };

  onSaved = (info) => {
    // refresh happens in the container; after a create land on the list (so the roster shows the
    // new person); after an edit stay on the editor. Bubble up so tree badges refresh.
    if (info && info.isNew) this.setState({ selected: null });
    if (this.props.onChanged) this.props.onChanged(info);
  };
  onDeleted = (info) => { this.setState({ selected: null }); if (this.props.onChanged) this.props.onChanged(info); };

  render() {
    const { scope } = this.props;
    const selected = this.state.selected;

    if (selected != null) {
      const row = selected === 'new' ? null : (this.props.users || []).find((u) => u.id === selected);
      return (
        <div className="tabeditor">
          <button type="button" className="linkback" onClick={this.back}><Icon name="chevronLeft" size={14} /> Back to users</button>
          <UserEditor
            key={selected}
            row={row}
            newCtx={selected === 'new' ? { customerId: scope.customerId || 0, subscriberId: scope.subscriberId || 0 } : undefined}
            authId={this.props.authId}
            userId={this.props.userId}
            subscriberId={this.props.selfSubscriberId}
            webToken={this.props.webToken}
            customers={this.props.customers}
            subscribers={this.props.subscribers}
            sites={this.props.sites}
            gates={this.props.gates}
            assignments={this.props.assignments}
            toast={this.props.toast}
            refreshUsers={this.props.refreshUsers}
            refreshAssignments={this.props.refreshAssignments}
            onSaved={this.onSaved}
            onDeleted={this.onDeleted}
            onCancelNew={this.cancel}
            onClose={this.cancel}
            onDirtyChange={this.props.onDirtyChange}
            onResult={this.props.onResult}
          />
        </div>
      );
    }

    const all = this.scopedUsers();
    const rf = this.state.roleFilter;
    const q = this.state.search.trim().toLowerCase();
    const assignedGuards = new Set((this.props.assignments || [])
      .filter((a) => !a.deletedAt)
      .map((a) => String(a.GateUserId != null ? a.GateUserId : a.GateUser && a.GateUser.id)));
    const isUnassigned = (u) => String(u.GateAcl && u.GateAcl.id) === '3' && !assignedGuards.has(String(u.id));
    // [86bcfugmv] a user's customer counts only if it's live (the user list carries deleted customers too)
    const liveCustomers = new Set((this.props.customers || []).map((c) => String(c.id)));
    const customerLive = (u) => !!u.GateCustomer && (liveCustomers.size === 0 || liveCustomers.has(String(u.GateCustomer.id)));
    const shown = all
      .filter((u) => !rf || (rf === 'unassigned' ? isUnassigned(u) : String(u.GateAcl && u.GateAcl.id) === String(rf)))
      .filter((u) => !q || (`${u.sFullName || ''} ${u.sUsername || ''}`).toLowerCase().includes(q))
      .slice()
      .sort((a, b) => {
        const rank = (u) => { const x = (u.GateAcl && u.GateAcl.id) || 99; return x === 3 ? 1000 : x; };
        return rank(a) !== rank(b) ? rank(a) - rank(b) : (a.sFullName || '').localeCompare(b.sFullName || '');
      });
    const count = (aclId) => all.filter((u) => u.GateAcl && String(u.GateAcl.id) === String(aclId)).length;
    const chips = [
      { key: '', label: `All (${all.length})` },
      { key: '1', label: `Account Admins (${count(1)})` },
      { key: '2', label: `Site Admins (${count(2)})` },
      { key: '3', label: `Guards (${count(3)})` },
      { key: '4', label: `Viewers (${count(4)})` },
      { key: 'unassigned', label: `Unassigned (${all.filter(isUnassigned).length})` },
    ].filter((c) => c.key === '' || c.key === 'unassigned' || parseInt(c.label.match(/\((\d+)\)/)[1], 10) > 0);

    return (
      <div className="tabpane">
        <p className="note">{scope.platform
          ? <>People with no subscriber: Dividia’s platform super users, plus any account that has lost its subscriber (flagged “No subscriber”). A flagged account can’t see any data until you open it and choose where it belongs.</>
          : <>Every person for this {scope.customerId ? 'customer' : 'subscriber'} — admins <b>and</b> guards — in one place. This is the “Users &amp; Guards” section, now living with its place.</>}</p>
        <div className="toolbar">
          <div className="rolechips">
            {chips.map((c) => (
              <button key={c.key} type="button" className={this.state.roleFilter === c.key ? 'on' : ''} onClick={() => this.setState({ roleFilter: c.key })}>{c.label}</button>
            ))}
          </div>
          {!scope.platform && <Button size="sm" icon={<Icon name="plus" size={14} />} onClick={() => this.open('new')}>Add user</Button>}
        </div>
        <div className="tabsearch">
          <TextInput search size="sm" value={this.state.search} onChange={(e) => this.setState({ search: e.target.value })} placeholder="Search people…" aria-label="Search people" />
        </div>
        {shown.length === 0 ? (
          <div className="sub" style={{ padding: 16, textAlign: 'center' }}>{all.length === 0 ? 'No users yet. Use “Add user” to create one.' : 'No people match this filter.'}</div>
        ) : (
          <div className="list">
            {shown.map((u) => {
              const acl = u.GateAcl && u.GateAcl.id;
              const isGuard = acl === 3;
              // [86bbxga1j] a web role (Site Admin/Viewer) with zero site grants can't see anything on the dashboard.
              const noWeb = (acl === 2 || acl === 4) && (u.GateUserSites || []).length === 0;
              // [86bcfugmv] a user missing the tenant its role needs can't see any data — flag it so it gets fixed
              const noSubscriber = acl !== 100 && !u.GateSubscriber;
              const noCustomer = !noSubscriber && [2, 3, 4].includes(acl) && !customerLive(u);
              return (
                <div className="item" key={u.id}>
                  <span className="ic"><Icon name="user" size={16} /></span>
                  <div className="grow">
                    <div className="p">{u.sFullName}</div>
                    <div className="s">{roleLabel(acl)}{u.sUsername ? ' · ' + u.sUsername : ''}</div>
                  </div>
                  {noSubscriber && <span className="minipill minipill--warn" title="No subscriber — this user can’t see any data. Open it and choose where it belongs.">No subscriber</span>}
                  {noCustomer && <span className="minipill minipill--warn" title="No customer — open it and choose the customer this user belongs to.">No customer</span>}
                  {noWeb && <span className="minipill minipill--warn" title="No site access — this user sees nothing on the web dashboard.">No web access</span>}
                  <span className={`minipill${isGuard ? ' on' : ''}`}>{roleLabel(acl)}</span>
                  <Button size="sm" variant="default" onClick={() => this.open(u.id)}>Edit</Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }
}
